import { ViewModelList } from 'infra/ViewModelList'
import { QuestionViewModel } from 'jobs/questions/component-registry'
import {
  PromptsReaderPort,
  PromptQuestionViewModelPort,
} from 'jobs/prompts/ports'
import { Prompt } from 'jobs/prompts/Prompt'
import { Subscription } from 'rxjs'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { action, computed, makeObservable } from 'mobx'
import Job from 'jobs/Job'
import { orderBy } from 'lodash'
import { skip } from 'rxjs/operators'

export class PromptsListViewModel {
  /**
   * View models.
   *
   * @private
   */
  private readonly _list = new ViewModelList<Prompt, PromptListItemViewModel>({
    models: () =>
      this.adapter
        .promptsForJob(this.job.id)
        // Exclude prompts we haven't fetched fully yet
        .filter((prompt) => prompt.question),
    create: (prompt) => new PromptListItemViewModel(this.adapter, prompt),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  /**
   * Scroll provider used to manage prompt scrolling state.
   */
  scrollProvider = new InfiniteScrollProvider({
    fetchItems: (params) =>
      this.adapter.fetchPromptsForJob({
        jobId: this.job.id?.toString() ?? '',
        limit: params.limit,
        after: params.after,
      }),
    limit: 10,
  })

  /**
   * Prompts list view model constructor.
   */
  constructor(
    private adapter: PromptQuestionViewModelPort & PromptsReaderPort,
    private job: Job
  ) {
    makeObservable(this)
  }

  /**
   * Activate the list.
   */
  activate() {
    this.scrollProvider.fetch().then()
    this._list.activate()
  }

  /**
   * Deactivate the list.
   */
  deactivate() {
    this._list.deactivate()
  }

  /**
   * Prompts for the current job.
   */
  @computed
  get prompts() {
    return orderBy(this._list.items, (p) => p.prompt.dateCreated, 'desc')
  }
}

export class PromptListItemViewModel {
  private _subscription?: Subscription

  /**
   * The prompt.
   */
  readonly prompt: Prompt

  /**
   * The question view model for the prompt's question.
   */
  question: QuestionViewModel

  /**
   * Prompts list view model constructor.
   */
  constructor(
    private adapter: PromptQuestionViewModelPort & PromptsReaderPort,
    prompt: Prompt
  ) {
    this.prompt = prompt
    this.question = this.adapter.createQuestionViewModel(prompt)
  }

  /**
   * Activates the View Model.
   */
  activate() {
    this._subscription = this.prompt.answer$
      .pipe(
        // Skip the first emission since we already create a question VM.
        skip(1)
      )
      .subscribe(
        action(() => {
          // TODO: Alternatively, we can expose hydrateAnswer or something so we
          // don't have to recreate it.
          this.question = this.adapter.createQuestionViewModel(this.prompt)
        })
      )
  }

  /**
   * Deactivates the View Model.
   */
  deactivate() {
    this._subscription?.unsubscribe()
  }
}
